import type { CanceledChunk, StreamingEvent } from '../../types/chat.types';
import type { ContentProcessorState } from '../contentProcessor.types';

import {
  createErrorDirective,
  createOutputLimitDirective,
  createWarningDirective,
} from '../../utils/directives';
import { parseErrorMessage } from '../../utils/errorsHelper';
import { appendText, appendTextToRoot } from '../nodes';

/** Default cancel copy: shown for server shutdown (SIGTERM) and any cancel
 *  whose reason is unknown/absent — we never surface deploy-specific text. */
export const INTERRUPTED_MESSAGE_TEXT = 'Interrupted';
/** Shown only when the user explicitly stops the turn (`user_requested`). */
export const USER_INTERRUPTED_MESSAGE_TEXT = 'User Interrupted';

/**
 * Centralized map of cancel `reason` → user-facing status label. This is the
 * single source of truth for cancel copy: add a new reason here and the
 * `Record` type forces every wire `CanceledReason` to be handled. Reasons not
 * listed (or absent) fall back to {@link INTERRUPTED_MESSAGE_TEXT}.
 */
const CANCELED_REASON_MESSAGES: Record<
  NonNullable<CanceledChunk['reason']>,
  string
> = {
  user_requested: USER_INTERRUPTED_MESSAGE_TEXT,
  shutdown: INTERRUPTED_MESSAGE_TEXT,
};

/** Resolve the status label for a cancel `reason`, falling back to the
 *  generic interrupted copy for unknown/absent reasons. */
export function getCanceledMessageText(
  reason?: CanceledChunk['reason'],
): string {
  return (
    (reason && CANCELED_REASON_MESSAGES[reason]) ?? INTERRUPTED_MESSAGE_TEXT
  );
}
export const OUTPUT_LIMIT_EXCEEDED_ERROR_TYPE = 'output_limit_exceeded';

/** User-facing messages for classified error types */
const CLASSIFIED_ERROR_MESSAGES: Record<string, string> = {
  overloaded:
    'Circuit is experiencing high demand. Please try again in a moment.',
  rate_limited:
    'You have been rate-limited. Please wait a moment and try again.',
  connection_interrupted:
    'The connection to the AI provider was interrupted. Please try again.',
};

export function handleErrorEvent(
  state: ContentProcessorState,
  event: StreamingEvent<'error'>,
): ContentProcessorState {
  const classifiedMessage =
    event.data.error_type && !state.options.isDebugMode
      ? CLASSIFIED_ERROR_MESSAGES[event.data.error_type]
      : undefined;

  const errorMessage =
    classifiedMessage ??
    parseErrorMessage(
      event.data.message,
      'An error occurred',
      state.options.isDebugMode,
    );

  let createDirective = createErrorDirective;
  if (event.data.error_type === OUTPUT_LIMIT_EXCEEDED_ERROR_TYPE) {
    createDirective = createOutputLimitDirective;
  } else if (event.data.retryable === true) {
    createDirective = createWarningDirective;
  }
  const directive = createDirective(errorMessage);

  return {
    ...appendText(state, directive),
    chunks: {
      ...state.chunks,
      progress: [],
      error: [...state.chunks.error, event.data],
    },
  };
}

export function handleCanceledEvent(
  state: ContentProcessorState,
  event: StreamingEvent<'canceled'>,
): ContentProcessorState {
  const messageText = getCanceledMessageText(event.data?.reason);
  const markdown = createWarningDirective(messageText);

  return {
    ...appendTextToRoot(state, markdown),
    chunks: {
      ...state.chunks,
      progress: [],
      canceled: [...state.chunks.canceled, event.data ?? {}],
    },
  };
}

export function handleWarningEvent(
  state: ContentProcessorState,
  event: StreamingEvent<'warning'>,
): ContentProcessorState {
  const markdown = createWarningDirective(event.data.message);

  return {
    ...appendText(state, markdown),
    chunks: {
      ...state.chunks,
      warning: [...state.chunks.warning, event.data],
    },
  };
}
